import { cn } from "@core-ui/utils/cn";
import t from "@ext/localization/locale/translate";
import { Button, type ButtonProps, LoadingButtonTemplate } from "@ui-kit/Button";
import { type ConfirmOptions, ConfirmPopover } from "@ui-kit/ConfirmPopover";
import { Divider } from "@ui-kit/Divider";
import { Icon } from "@ui-kit/Icon";
import {
	HOME_LAYOUT_EDIT_BAR_ANIMATION_DURATION,
	useHomeLayoutEditBarPresence,
} from "./hooks/useHomeLayoutEditBarPresence";
import type { HomeLayoutEditScope } from "./utils/homeLayoutTypes";

interface HomeLayoutEditBarProps {
	editScope: HomeLayoutEditScope | null;
	onCancel: () => void;
	onSave: () => void;
	cancelConfirmation?: ConfirmOptions;
	saveConfirmation?: ConfirmOptions;
	onAddSection?: () => void;
	isSaving?: boolean;
}

interface ConfirmableButtonProps extends Omit<ButtonProps, "onClick"> {
	confirmation?: ConfirmOptions;
	onClick: () => void;
}

const ConfirmableButton = ({ confirmation, onClick, ...buttonProps }: ConfirmableButtonProps) => {
	if (!confirmation) return <Button {...buttonProps} onClick={onClick} />;

	return (
		<ConfirmPopover {...confirmation} onConfirm={onClick}>
			<Button {...buttonProps} />
		</ConfirmPopover>
	);
};

const HomeLayoutEditBar = ({
	editScope,
	onCancel,
	onSave,
	cancelConfirmation,
	saveConfirmation,
	onAddSection,
	isSaving = false,
}: HomeLayoutEditBarProps) => {
	const { scope, presenceState } = useHomeLayoutEditBarPresence(editScope);
	if (!scope) return null;

	const title = scope === "personal" ? t("editing-personal-homepage-view") : t("editing-shared-homepage-view");
	return (
		<div className="pointer-events-none fixed inset-x-0 bottom-4 z-[var(--z-index-toolbar)] flex justify-center px-4">
			<div
				className={cn(
					"inline-flex max-w-full items-center gap-2 rounded-2xl bg-inverse-primary-bg py-2 pr-2 pl-4 text-inverse-primary-fg shadow-hard-base motion-reduce:animate-none",
					presenceState === "open"
						? "pointer-events-auto animate-in fade-in-0 slide-in-from-bottom-full ease-out"
						: "animate-out fade-out-0 slide-out-to-bottom-full ease-in [animation-fill-mode:forwards]",
				)}
				style={{ animationDuration: `${HOME_LAYOUT_EDIT_BAR_ANIMATION_DURATION}ms` }}
			>
				<div className="flex min-w-0 flex-1 items-center gap-2.5">
					<Icon
						className=" shrink-0 text-inverse-muted"
						icon={scope === "personal" ? "user-round" : "users"}
						size="sm"
					/>
					<span className="truncate text-xs font-medium">{title}</span>
				</div>

				<div className="flex shrink-0 items-center gap-2">
					<div className="flex items-center gap-1">
						<Divider className="h-5 bg-inverse-border max-[520px]:hidden" orientation="vertical" />
					</div>

					<div className="flex items-center gap-3">
						<Button
							className="!bg-transparent !text-inverse-secondary-fg hover:!bg-inverse-hover hover:!text-inverse-primary-fg"
							disabled={!onAddSection || isSaving}
							onClick={onAddSection}
							size="xs"
							startIcon="plus"
							variant="ghost"
						>
							{t("add-section")}
						</Button>
						<ConfirmableButton
							className="!bg-transparent !text-inverse-secondary-fg hover:!bg-inverse-hover hover:!text-inverse-primary-fg"
							confirmation={cancelConfirmation}
							disabled={isSaving}
							onClick={onCancel}
							size="xs"
							variant="ghost"
						>
							{t("cancel")}
						</ConfirmableButton>
						{isSaving ? (
							<LoadingButtonTemplate
								className="[&>div]:flex-row-reverse !bg-[hsl(var(--inverse-accent))] !text-[hsl(var(--inverse-accent-text))]"
								size="xs"
								text={t("save")}
							/>
						) : (
							<ConfirmableButton
								className="!bg-[hsl(var(--inverse-accent))] !text-[hsl(var(--inverse-accent-text))] hover:!bg-[hsl(var(--inverse-accent-hover))]"
								confirmation={saveConfirmation}
								onClick={onSave}
								size="xs"
							>
								{t("save")}
							</ConfirmableButton>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default HomeLayoutEditBar;
